/**
 * OccupationalRisk Types for MEV-Med Frontend
 * Type definitions for occupational risk management (NR-09, NR-15, NR-16)
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de riscos ocupacionais
 */

import type { RiskMatrixType, RiskLevel } from './riskMatrix';

export const EXAM_TYPES = [
  'admissional',
  'periodico',
  'demissional',
  'complementar',
  'retorno_trabalho',
  'mudanca_risco',
  'consulta_clinica',
] as const;
export type ExamType = typeof EXAM_TYPES[number];

/**
 * Os tipos de documento vêm do catálogo do backend (`backend/src/documents/<tipo>/`),
 * pelo arquivo gerado por `npm run documents:sync`. Reexportados daqui para que os
 * arquivos que já importavam estes nomes não mudem de import.
 */
export { REPORT_TYPES, OCC_REPORT_TYPE_LABELS } from './documentTypes.generated';
export type { ReportType } from './documentTypes.generated';

export const EXPOSURE_FORMS = [
  'inalacao', 'contato_direto', 'ingestao', 'cutanea', 'mucosas', 'outros',
] as const;
export type ExposureForm = typeof EXPOSURE_FORMS[number];

export const EXPOSURE_TYPES = [
  'continua', 'intermitente', 'eventual', 'habitual_permanente',
] as const;
export type ExposureType = typeof EXPOSURE_TYPES[number];

/**
 * Localized labels for the enum unions above. Centralized so form, details
 * page, and step components share a single source of truth.
 */
export const OCC_EXAM_TYPE_LABELS: Record<ExamType, string> = {
  admissional: 'Admissional',
  periodico: 'Periódico',
  demissional: 'Demissional',
  complementar: 'Complementar',
  retorno_trabalho: 'Retorno ao Trabalho',
  mudanca_risco: 'Mudança de Risco',
  consulta_clinica: 'Consulta Clínica',
};

export const OCC_EXPOSURE_FORM_LABELS: Record<ExposureForm, string> = {
  inalacao: 'Inalação',
  contato_direto: 'Contato direto',
  ingestao: 'Ingestão',
  cutanea: 'Cutânea',
  mucosas: 'Mucosas',
  outros: 'Outros',
};

export const OCC_EXPOSURE_TYPE_LABELS: Record<ExposureType, string> = {
  continua: 'Contínua',
  intermitente: 'Intermitente',
  eventual: 'Eventual',
  habitual_permanente: 'Habitual e permanente',
};

// #306 — grau de insalubridade (NR-15)
export const UNHEALTHY_DEGREES = ['MINIMO', 'MEDIO', 'MAXIMO'] as const;
export type UnhealthyDegree = typeof UNHEALTHY_DEGREES[number];

export const OCC_UNHEALTHY_DEGREE_LABELS: Record<UnhealthyDegree, string> = {
  MINIMO: 'Mínimo (10%)',
  MEDIO: 'Médio (20%)',
  MAXIMO: 'Máximo (40%)',
};

// #308 — natureza da avaliação
export const ASSESSMENT_TYPES = ['QUALITATIVE', 'QUANTITATIVE'] as const;
export type AssessmentType = typeof ASSESSMENT_TYPES[number];

export const OCC_ASSESSMENT_TYPE_LABELS: Record<AssessmentType, string> = {
  QUALITATIVE: 'Qualitativa',
  QUANTITATIVE: 'Quantitativa',
};

// #309a — via de penetração (seletor)
export const PENETRATION_ROUTES = [
  'Auditiva', 'Cutânea', 'Digestiva', 'Ocular', 'Parenteral', 'Respiratória', 'Sistêmica',
] as const;

/**
 * Enum for OccupationalRisk status
 */
export enum OccupationalRiskStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

/**
 * Enum for Risk Type - uses backend standard values
 */
export enum RiskType {
  PHYSICAL = 'FISICO',
  CHEMICAL = 'QUIMICO',
  BIOLOGICAL = 'BIOLOGICO',
  ERGONOMIC = 'ERGONOMICO',
  ACCIDENT = 'ACIDENTE',
  OTHER = 'OUTRO'
}

/**
 * Risk classification (calculated by backend)
 */
export interface RiskClassification {
  type: string;
  isHazardous: boolean;
  isUnhealthy: boolean;
  hasSocialSecurityAgent: boolean;
}

/**
 * Raw API response shape for OccupationalRisk.
 */
export type OccupationalRiskApiResponse = OccupationalRisk;

/**
 * OccupationalRisk entity interface (normalized domain shape)
 */
export interface OccupationalRisk {
  id: number;
  risk: string;
  riskFactorName: string;
  hazard: string;
  riskType: RiskType;
  esocialAgentCode?: string | null;
  esocialAgentCodeLegacy?: string | null;
  // #473 — "este risco não é agente nocivo do eSocial", dito por uma pessoa. É estado
  // próprio, e não o código 09.01.001: aquele descreve o trabalhador, não o risco.
  esocialAgentCodeNotApplicable?: boolean;
  actionLevel?: string | null;
  toleranceLimit?: string | null;
  measurementUnit?: string | null;
  evaluationMethod?: string | null;
  defaultPeriodicity?: string | null;
  penetrationRoute?: string | null;
  propagationMedium?: string | null;
  unhealthyAgent?: string | null;
  hazardousAgent?: string | null;
  socialSecurityAgent?: string | null;
  evaluationMethodology?: string | null;
  legalFoundation?: string | null;
  healthDamages?: string | null;
  determiningFactors?: string | null;
  emergencyPreparation?: string | null;
  exposureForm?: string | null; // #309b — texto livre
  exposureType?: ExposureType | null;
  riskMatrixType?: RiskMatrixType | null;
  riskProbability?: number | null;
  riskSeverity?: number | null;
  unhealthyDegree?: string | null;
  assessmentType?: string | null;
  // #417 — catálogo de agentes nocivos. `hazardousAgentId` é a ligação com a ficha;
  // os demais são o que ela preencheu, e continuam editáveis à mão.
  hazardousAgentId?: number | null;
  inssAgentCode?: string | null;
  nr15Annex?: number | null;
  nr16Annex?: string | null;
  specialRetirementYears?: number | null;
  ceilingValue?: boolean;
  skinAbsorption?: boolean;
  riskLevel?: RiskLevel | null;
  status: OccupationalRiskStatus;
  classification?: RiskClassification;
  // Alert counts (optional - returned when includeCounts=true)
  _counts?: {
    companies?: number;
    departments?: number;
    occupations?: number;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

/**
 * Filters for listing occupational risks
 */
export interface OccupationalRiskFilters {
  search?: string;
  riskType?: RiskType;
  unhealthyAgent?: boolean;
  hazardousAgent?: boolean;
  socialSecurityAgent?: boolean;
  esocialAgentCode?: boolean;
  status?: OccupationalRiskStatus;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * #473 — uma entrada do lote de conclusão do código da Tabela 24. Código e "não se aplica"
 * são mutuamente exclusivos, e uma entrada inválida reprova o lote inteiro: o backend não
 * grava metade. Teto de 200 entradas por chamada.
 */
export interface EsocialAgentCodeBulkEntry {
  id: number;
  esocialAgentCode?: string;
  naoSeAplica?: boolean;
}

/**
 * Form data for creating/updating occupational risks
 * This represents the UI form state (with booleans for checkboxes)
 */
export interface OccupationalRiskFormData {
  risk: string;
  riskFactorName: string;
  hazard: string;
  riskType: RiskType;
  esocialAgentCode?: string;
  esocialAgentCodeNotApplicable?: boolean;
  actionLevel?: string;
  toleranceLimit?: string;
  measurementUnit?: string;
  evaluationMethod?: string;
  defaultPeriodicity?: string;
  penetrationRoute?: string;
  propagationMedium?: string;
  unhealthyAgent: boolean;
  hazardousAgent: boolean;
  socialSecurityAgent: boolean;
  evaluationMethodology?: string;
  legalFoundation?: string;
  healthDamages?: string;
  determiningFactors?: string;
  emergencyPreparation?: string;
  exposureForm?: string;
  exposureType?: ExposureType;
  riskMatrixType?: RiskMatrixType | null;
  riskProbability?: number | null;
  riskSeverity?: number | null;
  unhealthyDegree?: string;
  assessmentType?: string;
  hazardousAgentId?: number | null;
  inssAgentCode?: string;
  nr15Annex?: number | null;
  nr16Annex?: string;
  specialRetirementYears?: number | null;
  ceilingValue: boolean;
  skinAbsorption: boolean;
  status: OccupationalRiskStatus;
}

/**
 * API payload for creating/updating occupational risks
 * This represents what the backend expects (with strings for NR codes)
 */
export interface OccupationalRiskApiPayload {
  risk: string;
  riskFactorName: string;
  hazard: string;
  riskType: string;
  esocialAgentCode?: string;
  esocialAgentCodeNotApplicable?: boolean;
  actionLevel?: string;
  toleranceLimit?: string;
  measurementUnit?: string;
  evaluationMethod?: string;
  defaultPeriodicity?: string;
  penetrationRoute?: string;
  propagationMedium?: string;
  unhealthyAgent?: string;
  hazardousAgent?: string;
  socialSecurityAgent?: string;
  evaluationMethodology?: string;
  legalFoundation?: string;
  healthDamages?: string;
  determiningFactors?: string;
  emergencyPreparation?: string;
  exposureForm?: string;
  exposureType?: ExposureType;
  riskMatrixType?: RiskMatrixType | null;
  riskProbability?: number | null;
  riskSeverity?: number | null;
  unhealthyDegree?: string;
  assessmentType?: string;
  hazardousAgentId?: number | null;
  inssAgentCode?: string;
  nr15Annex?: number | null;
  nr16Annex?: string;
  specialRetirementYears?: number | null;
  ceilingValue: boolean;
  skinAbsorption: boolean;
  status: OccupationalRiskStatus;
}

/**
 * Response for list endpoint with pagination
 */
export interface OccupationalRiskListResponse {
  risks: OccupationalRisk[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Dependency check response
 */
export interface OccupationalRiskDependencies {
  hasApplications: boolean;
  count: number;
  details?: {
    companies?: number;
    departments?: number;
    occupations?: number;
    employees?: number;
  };
}

/**
 * Classification result from risk analysis
 */
export interface OccupationalRiskClassification {
  type: RiskType;
  isHazardous: boolean;
  isUnhealthy: boolean;
  requiresSocialSecurity: boolean;
  regulatoryFramework: string[];
}

/**
 * Props for UI components
 */
export interface OccupationalRiskTableProps {
  risks: OccupationalRisk[];
  onEdit: (risk: OccupationalRisk) => void;
  onDelete: (id: number) => void;
  onView: (risk: OccupationalRisk) => void;
  loading?: boolean;
}

export interface OccupationalRiskFormProps {
  initialData?: OccupationalRisk;
  onSubmit: (data: OccupationalRiskFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export interface OccupationalRiskFiltersProps {
  filters: OccupationalRiskFilters;
  onChange: (filters: OccupationalRiskFilters) => void;
}

export interface RiskTypeChipProps {
  type: RiskType;
  size?: 'small' | 'medium';
}
